/**
 * Vendas e propostas (F3c) — porte de src/pages/Vendas do app atual, mesmas regras
 * (packages/core/services/vendaService). Vendas é só comercial: confirmar/estornar
 * são transições de status e NÃO mexem no estoque (a baixa é a alocação em
 * Importações). A proposta em PDF sai do MESMO gerador do app atual
 * (src/lib/pdf.ts → gerarPropostaPDF), importado sem cópia: layout idêntico.
 */
import { createMemo, createResource, createSignal, For, Index, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import type { Cliente, FormaPagamento, ModeloLista, StatusVenda, VendaDetalhe } from '@shared/types'
import { vendaSchema } from '@shared/validation'
import { gerarPropostaPDF } from '../../../../src/lib/pdf'
import { api, versaoDados } from '../nucleo/cliente'
import { mensagemErro } from '../lib/erro'
import { AreaTexto, brl, Campo, confirmar, criarRecarga, dataBR, Erro, Etiqueta, Formulario, ListaDetalhe, num, Painel, Secao, Selecao, T, type Tom, Vazio } from '../ui/kit'

const STATUS: StatusVenda[] = ['ORCAMENTO', 'CONFIRMADO', 'FATURADO', 'ENTREGUE', 'CANCELADO']
const TOM: Record<StatusVenda, Tom> = { ORCAMENTO: 'neutro', CONFIRMADO: 'sucesso', FATURADO: 'destaque', ENTREGUE: 'aviso', CANCELADO: 'perigo' }
const FORMAS: FormaPagamento[] = ['PIX', 'BOLETO', 'TRANSFERENCIA', 'CARTAO', 'OUTRO']

export function Vendas(props: { podeEditar: boolean }) {
  const [status, setStatus] = createSignal<StatusVenda | ''>('')
  const [detalheId, setDetalheId] = createSignal<number | null>(null)
  const [form, setForm] = createSignal<{ det: VendaDetalhe | null } | null>(null)
  const [recarga, recarregar] = criarRecarga()
  const [lista] = createResource(() => ({ s: status(), v: versaoDados(), r: recarga() }), (k) => api.vendas.list(k.s || undefined))

  const listaEl = (
    <div class="flex flex-col gap-4 p-4 sm:p-6">
      <div class="flex flex-wrap items-center gap-3">
        <div class="mr-auto">
          <h1 class="text-xl font-semibold text-ink">Vendas e propostas</h1>
          <p class="text-xs text-ink-subtle">Comercial: confirmar/estornar não mexem no estoque (a baixa é pela alocação em Importações).</p>
        </div>
        <Show when={props.podeEditar}>
          <Button variant="accent" size="sm" onClick={() => setForm({ det: null })}>Novo orçamento</Button>
        </Show>
      </div>
      <Selecao class="w-48" value={status()} onChange={(e) => setStatus(e.currentTarget.value as StatusVenda | '')} aria-label="Status">
        <option value="">Todos os status</option>
        <For each={STATUS}>{(s) => <option value={s}>{s}</option>}</For>
      </Selecao>
      <Show when={(lista.latest ?? []).length > 0} fallback={<Vazio titulo={lista.loading ? 'Carregando…' : 'Nenhuma venda'} texto="Crie um orçamento para um cliente." />}>
        <div class={T.caixa}>
          <table class={T.tabela}>
            <thead class={T.cab}>
              <tr>
                <th class={T.th}>Pedido</th>
                <th class={T.th}>Cliente</th>
                <th class={T.th}>Status</th>
                <th class={`${T.th} text-right hidden md:table-cell`}>Itens</th>
                <th class={`${T.th} text-right`}>Total</th>
                <th class={`${T.th} hidden md:table-cell`}>Data</th>
              </tr>
            </thead>
            <tbody>
              <For each={lista.latest}>
                {(v) => (
                  <tr class={`${T.tr} cursor-pointer`} classList={{ 'bg-hover': detalheId() === v.id }} onClick={() => setDetalheId(v.id)}>
                    <td class={`${T.td} font-medium text-ink`}>{v.numeroPedido}</td>
                    <td class={`${T.td} text-ink-muted`}>{v.clienteNome}</td>
                    <td class={T.td}><Etiqueta tom={TOM[v.status]}>{v.status}</Etiqueta></td>
                    <td class={`${T.td} text-right tabular-nums text-ink hidden md:table-cell`}>{v.qtdItens}</td>
                    <td class={`${T.td} text-right tabular-nums text-ink`}>{brl(v.valorTotal)}</td>
                    <td class={`${T.td} text-ink-subtle hidden md:table-cell`}>{dataBR(v.dataVenda)}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  )

  return (
    <>
      <ListaDetalhe
        id="vendas"
        lista={listaEl}
        larguraDetalhe={620}
        detalhe={
          detalheId() !== null && (
            <DetalheVenda
              id={detalheId()!}
              podeEditar={props.podeEditar}
              recarga={recarga()}
              aoFechar={() => setDetalheId(null)}
              aoMudar={recarregar}
              aoExcluida={() => (setDetalheId(null), recarregar())}
              aoEditar={(det) => setForm({ det })}
            />
          )
        }
        aoFecharDetalhe={() => setDetalheId(null)}
      />
      <Show when={form()}>
        <FormVenda venda={form()!.det} aoFechar={() => setForm(null)} aoSalvar={() => (setForm(null), recarregar())} />
      </Show>
    </>
  )
}

function DetalheVenda(props: {
  id: number
  podeEditar: boolean
  recarga: number
  aoFechar: () => void
  aoMudar: () => void
  aoExcluida: () => void
  aoEditar: (d: VendaDetalhe) => void
}) {
  const [local, recarregar] = criarRecarga()
  const [d] = createResource(() => ({ id: props.id, v: versaoDados(), r: props.recarga, l: local() }), ({ id }) => api.vendas.get(id))
  const [erro, setErro] = createSignal<string | null>(null)
  const [processando, setProcessando] = createSignal(false)
  const [proposta, setProposta] = createSignal(false)
  const executar = async (fn: () => Promise<unknown>, depois?: () => void) => {
    setErro(null)
    setProcessando(true)
    try {
      await fn()
      recarregar()
      props.aoMudar()
      depois?.()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setProcessando(false)
    }
  }
  const pedir = async (titulo: string, texto: string, fn: () => Promise<unknown>, opc: { perigo?: boolean; rotulo?: string; depois?: () => void } = {}) => {
    if (await confirmar(titulo, texto, { rotulo: opc.rotulo, perigo: opc.perigo })) void executar(fn, opc.depois)
  }

  return (
    <Show when={d.latest} fallback={<p class="p-5 text-sm text-ink-subtle">{d.loading ? 'Carregando…' : 'Venda não encontrada.'}</p>}>
      {(v) => (
        <Painel
          titulo={
            <span class="flex flex-wrap items-center gap-2">
              {v().numeroPedido}
              <Etiqueta tom={TOM[v().status]}>{v().status}</Etiqueta>
            </span>
          }
          sub={
            <>
              {v().clienteNome} · Data: {dataBR(v().dataVenda)}
              <Show when={v().vendedorNome}> · Vendedor: {v().vendedorNome}</Show>
              <Show when={v().formaPagamento}> · {v().formaPagamento} · {v().condicaoPagamento || '—'}</Show>
            </>
          }
          aoFechar={props.aoFechar}
          rodape={
            <>
              <Button variant="secondary" size="sm" onClick={() => setProposta(true)}>Gerar proposta (PDF)</Button>
              <Show when={props.podeEditar}>
                <span class="ml-auto" />
                <Show when={v().status === 'ORCAMENTO'}>
                  <Button variant="ghost" size="sm" disabled={processando()} onClick={() => props.aoEditar(v())}>Editar</Button>
                  <Button variant="danger-ghost" size="sm" disabled={processando()} onClick={() => void pedir('Excluir orçamento?', 'Excluir este orçamento?', () => api.vendas.excluir(props.id), { perigo: true, rotulo: 'Excluir', depois: props.aoExcluida })}>
                    Excluir
                  </Button>
                </Show>
                <Show when={v().status === 'CONFIRMADO'}>
                  <Button variant="ghost" size="sm" disabled={processando()} onClick={() => void executar(() => api.vendas.setStatus(props.id, 'FATURADO'))}>Faturar</Button>
                </Show>
                <Show when={v().status === 'FATURADO'}>
                  <Button variant="ghost" size="sm" disabled={processando()} onClick={() => void executar(() => api.vendas.setStatus(props.id, 'ENTREGUE'))}>Entregar</Button>
                </Show>
                <Show when={['CONFIRMADO', 'FATURADO', 'ENTREGUE'].includes(v().status)}>
                  <Button variant="danger-ghost" size="sm" disabled={processando()} onClick={() => void pedir('Estornar a venda?', 'Estornar a venda? (Não mexe no estoque.)', () => api.vendas.estornar(props.id), { perigo: true, rotulo: 'Estornar' })}>
                    Estornar
                  </Button>
                </Show>
                <Show when={v().status === 'ORCAMENTO'}>
                  <Button variant="accent" size="sm" disabled={processando()} onClick={() => void pedir('Confirmar venda?', 'Confirmar esta venda?', () => api.vendas.confirmar(props.id), { rotulo: 'Confirmar' })}>
                    Confirmar venda
                  </Button>
                </Show>
              </Show>
            </>
          }
        >
          <Erro mensagem={erro()} />
          <div class="rounded-xl border border-edge overflow-auto">
            <table class={T.tabela}>
              <thead class="text-left text-xs text-ink-subtle border-b border-edge">
                <tr>
                  <th class="px-3 py-2 font-medium">Modelo</th>
                  <th class="px-3 py-2 font-medium text-right">Qtd</th>
                  <th class="px-3 py-2 font-medium text-right">Preço un.</th>
                  <th class="px-3 py-2 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <For each={v().itens}>
                  {(it) => (
                    <tr class="border-t border-edge-muted">
                      <td class="px-3 py-1.5 text-ink">
                        {it.codigoModelo}
                        <Show when={it.serializado === 1}><span class="ml-1"><Etiqueta tom="aviso">série</Etiqueta></span></Show>
                      </td>
                      <td class="px-3 py-1.5 text-right tabular-nums text-ink">{it.quantidade}</td>
                      <td class="px-3 py-1.5 text-right tabular-nums text-ink-muted">{brl(it.precoUnitario)}</td>
                      <td class="px-3 py-1.5 text-right tabular-nums text-ink">{brl(it.precoUnitario * it.quantidade - it.descontoItem)}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
          <div class="flex justify-end gap-6 text-sm">
            <Show when={v().desconto > 0}><span class="text-ink-muted">Desconto: {brl(v().desconto)}</span></Show>
            <span class="text-ink-muted">Total: <span class="font-semibold text-ink tabular-nums">{brl(v().valorTotal)}</span></span>
          </div>
          <Show when={proposta()}>
            <GerarProposta vendaId={props.id} condicaoPadrao={v().condicaoPagamento ?? ''} aoFechar={() => setProposta(false)} />
          </Show>
        </Painel>
      )}
    </Show>
  )
}

/** Igual ao PropostaGerarModal do app atual: euro, condição e data base → gerarPropostaPDF. */
function GerarProposta(props: { vendaId: number; condicaoPadrao: string; aoFechar: () => void }) {
  const hoje = new Date().toISOString().slice(0, 10)
  const [euro, setEuro] = createSignal('6,40')
  const [pagamento, setPagamento] = createSignal(props.condicaoPadrao || '')
  const [dataBase, setDataBase] = createSignal(hoje)
  const [erro, setErro] = createSignal<string | null>(null)
  const [gerando, setGerando] = createSignal(false)
  const gerar = async () => {
    setErro(null)
    const euroNum = Number(euro().trim().replace(',', '.'))
    if (!Number.isFinite(euroNum) || euroNum <= 0) return setErro('Informe uma cotação do euro válida.')
    setGerando(true)
    try {
      const dados = await api.vendas.dadosProposta(props.vendaId)
      if (!dados) return setErro('Proposta não encontrada.')
      gerarPropostaPDF(dados, { euroRate: euroNum, condicaoPagamento: pagamento(), dataReajusteBase: dataBase().split('-').reverse().join('.') })
      props.aoFechar()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setGerando(false)
    }
  }
  return (
    <Formulario
      aberto
      titulo="Gerar proposta (PDF)"
      largura={440}
      aoFechar={props.aoFechar}
      aoEnviar={() => void gerar()}
      rodape={
        <>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={gerando()}>{gerando() ? 'Gerando…' : 'Gerar PDF'}</Button>
        </>
      }
    >
      <Erro mensagem={erro()} />
      <Campo rotulo="Cotação do euro (R$)" dica="Inserida na cláusula de reajuste da proposta">
        <Input value={euro()} onInput={(e) => setEuro(e.currentTarget.value)} placeholder="6,40" />
      </Campo>
      <Campo rotulo="Condição de pagamento">
        <Input value={pagamento()} onInput={(e) => setPagamento(e.currentTarget.value)} placeholder="20% a vista / 80% - 28 DDL" />
      </Campo>
      <Campo rotulo="Data base do reajuste">
        <Input type="date" value={dataBase()} onInput={(e) => setDataBase(e.currentTarget.value)} />
      </Campo>
    </Formulario>
  )
}

type LinhaItem = {
  modeloId: number; codigoModelo: string; serializado: number; quantidade: string; precoUnitario: string; descontoItem: string
  icmsPercent: string; ipiPercent: string; prazoEntrega: string; especificacao: string
}
const numero = (s: string) => {
  const n = Number(s.trim().replace(',', '.'))
  return Number.isFinite(n) ? n : NaN
}

function FormVenda(props: { venda: VendaDetalhe | null; aoFechar: () => void; aoSalvar: () => void }) {
  const v = props.venda
  const [clientes] = createResource(() => api.clientes.list())
  const [modelos] = createResource(() => api.catalogo.modelos({}))
  const [clienteId, setClienteId] = createSignal<number>(v?.clienteId ?? 0)
  const [buscaCliente, setBuscaCliente] = createSignal('')
  const [buscaModelo, setBuscaModelo] = createSignal('')
  const [forma, setForma] = createSignal<string>(v?.formaPagamento ?? '')
  const [condicao, setCondicao] = createSignal(v?.condicaoPagamento ?? '')
  const [desconto, setDesconto] = createSignal((v?.desconto ?? 0).toString())
  const [obs, setObs] = createSignal(v?.observacoes ?? '')
  const [numeroProposta, setNumeroProposta] = createSignal(v?.numeroProposta ?? '')
  const [localidade, setLocalidade] = createSignal(v?.localidade ?? '')
  const [projeto, setProjeto] = createSignal(v?.nomeProjeto ?? '')
  const [contatoNome, setContatoNome] = createSignal(v?.contatoNome ?? '')
  const [contatoCargo, setContatoCargo] = createSignal(v?.contatoCargo ?? '')
  const [contatoTel, setContatoTel] = createSignal(v?.contatoTelefone ?? '')
  const [contatoEmail, setContatoEmail] = createSignal(v?.contatoEmail ?? '')
  const [itens, setItens] = createSignal<LinhaItem[]>(
    v?.itens.map((it) => ({
      modeloId: it.modeloId, codigoModelo: it.codigoModelo, serializado: it.serializado,
      quantidade: it.quantidade.toString(), precoUnitario: it.precoUnitario.toString(), descontoItem: (it.descontoItem ?? 0).toString(),
      icmsPercent: it.icmsPercent != null ? String(it.icmsPercent) : '', ipiPercent: it.ipiPercent != null ? String(it.ipiPercent) : '',
      prazoEntrega: it.prazoEntrega ?? '', especificacao: it.especificacao ?? '',
    })) ?? [],
  )
  const [erro, setErro] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)

  const cliente = () => (clientes() ?? []).find((c) => c.id === clienteId())
  const achadosCliente = createMemo(() => {
    const t = buscaCliente().trim().toLowerCase()
    if (!t || cliente()) return []
    return (clientes() ?? []).filter((c: Cliente) => c.razaoSocial.toLowerCase().includes(t) || c.cnpjCpf.includes(t)).slice(0, 6)
  })
  const achadosModelo = createMemo(() => {
    const t = buscaModelo().trim().toLowerCase()
    if (!t) return []
    return (modelos() ?? []).filter((m) => m.codigoModelo.toLowerCase().includes(t)).slice(0, 6)
  })
  const adicionar = (m: ModeloLista) => {
    if (itens().some((it) => it.modeloId === m.id)) return
    setItens([
      ...itens(),
      {
        modeloId: m.id, codigoModelo: m.codigoModelo, serializado: m.serializado, quantidade: '1',
        precoUnitario: (m.precoVendaPadrao ?? 0).toString(), descontoItem: '0', icmsPercent: '', ipiPercent: '',
        prazoEntrega: '', especificacao: m.descricaoCurta ?? '',
      },
    ])
    setBuscaModelo('')
  }
  const mudar = (i: number, p: Partial<LinhaItem>) => setItens(itens().map((it, j) => (j === i ? { ...it, ...p } : it)))
  const total = createMemo(() => {
    const sub = itens().reduce((s, it) => s + (numero(it.precoUnitario) || 0) * (numero(it.quantidade) || 0) - (numero(it.descontoItem) || 0), 0)
    return Math.max(0, sub - (numero(desconto()) || 0))
  })

  const salvar = async () => {
    setErro(null)
    const pct = (s: string) => (s.trim() === '' ? null : numero(s))
    const payload = {
      clienteId: clienteId(),
      formaPagamento: forma() || null,
      condicaoPagamento: condicao() || null,
      desconto: numero(desconto() || '0'),
      observacoes: obs() || null,
      numeroProposta: numeroProposta() || null,
      localidade: localidade() || null,
      nomeProjeto: projeto() || null,
      contatoNome: contatoNome() || null,
      contatoCargo: contatoCargo() || null,
      contatoTelefone: contatoTel() || null,
      contatoEmail: contatoEmail() || null,
      itens: itens().map((it) => ({
        modeloId: it.modeloId, quantidade: Math.floor(numero(it.quantidade)), precoUnitario: numero(it.precoUnitario),
        descontoItem: numero(it.descontoItem || '0'), icmsPercent: pct(it.icmsPercent), ipiPercent: pct(it.ipiPercent),
        prazoEntrega: it.prazoEntrega || null, especificacao: it.especificacao || null,
      })),
    }
    const r = vendaSchema.safeParse(payload)
    if (!r.success) return setErro(r.error.issues[0]?.message ?? 'Dados inválidos.')
    setSalvando(true)
    try {
      if (v) await api.vendas.update(v.id, r.data)
      else await api.vendas.create(r.data)
      props.aoSalvar()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Formulario
      aberto
      titulo={v ? `Editar ${v.numeroPedido}` : 'Novo orçamento'}
      largura={880}
      aoFechar={props.aoFechar}
      aoEnviar={() => void salvar()}
      rodape={
        <>
          <span class="mr-auto self-center text-sm text-ink-muted">Total: <b class="text-ink tabular-nums">{brl(total())}</b></span>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={salvando()}>{salvando() ? 'Salvando…' : 'Salvar'}</Button>
        </>
      }
    >
      <Erro mensagem={erro()} />
      <Show
        when={cliente()}
        fallback={
          <Campo rotulo="Cliente">
            <div class="relative">
              <Input placeholder="Buscar por nome ou CNPJ/CPF…" value={buscaCliente()} onInput={(e) => setBuscaCliente(e.currentTarget.value)} />
              <Show when={achadosCliente().length > 0}>
                <div class="absolute z-20 mt-1 w-full max-w-md overflow-hidden rounded-md border border-edge bg-panel shadow-lg">
                  <For each={achadosCliente()}>
                    {(c) => (
                      <button type="button" class="block w-full px-3 py-1.5 text-left text-sm text-ink hover:bg-hover" onClick={() => (setClienteId(c.id), setBuscaCliente(''))}>
                        {c.razaoSocial} <span class="text-xs text-ink-subtle">{c.cnpjCpf}</span>
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </Campo>
        }
      >
        <div class="flex items-center justify-between rounded-md border border-edge px-3 py-2 text-sm">
          <span class="text-ink">{cliente()!.razaoSocial}</span>
          <Show when={!v}>
            <Button type="button" variant="ghost" size="sm" onClick={() => setClienteId(0)}>trocar</Button>
          </Show>
        </div>
      </Show>

      <Secao titulo="Dados da proposta">
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Campo rotulo="Nº da proposta"><Input value={numeroProposta()} onInput={(e) => setNumeroProposta(e.currentTarget.value)} placeholder="026.0678.REV4" /></Campo>
          <Campo rotulo="Localidade"><Input value={localidade()} onInput={(e) => setLocalidade(e.currentTarget.value)} placeholder="Ourinhos - SP" /></Campo>
          <Campo class="col-span-2" rotulo="Nome do projeto"><Input value={projeto()} onInput={(e) => setProjeto(e.currentTarget.value)} /></Campo>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Campo rotulo="Contato (A/C)"><Input value={contatoNome()} onInput={(e) => setContatoNome(e.currentTarget.value)} placeholder="Nome" /></Campo>
          <Campo rotulo="Cargo / Depto"><Input value={contatoCargo()} onInput={(e) => setContatoCargo(e.currentTarget.value)} /></Campo>
          <Campo rotulo="Telefone"><Input value={contatoTel()} onInput={(e) => setContatoTel(e.currentTarget.value)} /></Campo>
          <Campo rotulo="E-mail"><Input value={contatoEmail()} onInput={(e) => setContatoEmail(e.currentTarget.value)} /></Campo>
        </div>
      </Secao>

      <div class="flex flex-col gap-1">
        <span class="text-xs text-ink-muted">Itens</span>
        <div class="rounded-lg border border-edge">
          <Show when={itens().length > 0} fallback={<div class="px-3 py-3 text-sm text-ink-subtle">Nenhum item. Busque um modelo abaixo.</div>}>
            <div class="flex items-center gap-2 border-b border-edge px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
              <div class="flex-1">Modelo</div>
              <div class="w-16">Qtd</div>
              <div class="w-28">Preço un. (R$)</div>
              <div class="w-24">Desconto (R$)</div>
              <div class="w-8" />
            </div>
          </Show>
          <Index each={itens()}>
            {(it, i) => (
              <div class="border-b border-edge-muted px-3 py-2 last:border-0">
                <div class="flex items-center gap-2">
                  <div class="flex-1 text-sm text-ink">
                    {it().codigoModelo}
                    <Show when={it().serializado === 1}><span class="ml-1"><Etiqueta tom="aviso">série</Etiqueta></span></Show>
                  </div>
                  <Input class="w-16" value={it().quantidade} title="Quantidade" placeholder="Qtd" onInput={(e) => mudar(i, { quantidade: e.currentTarget.value })} />
                  <Input class="w-28" value={it().precoUnitario} title="Preço unitário (R$)" placeholder="Preço un." onInput={(e) => mudar(i, { precoUnitario: e.currentTarget.value })} />
                  <Input class="w-24" value={it().descontoItem} title="Desconto do item (R$)" placeholder="Desconto" onInput={(e) => mudar(i, { descontoItem: e.currentTarget.value })} />
                  <Button type="button" variant="ghost" size="sm" aria-label="Remover item" onClick={() => setItens(itens().filter((_, j) => j !== i))}>✕</Button>
                </div>
                <div class="mt-1.5 flex flex-wrap items-center gap-2 pl-1">
                  <Input class="w-20" value={it().icmsPercent} title="ICMS %" placeholder="ICMS %" onInput={(e) => mudar(i, { icmsPercent: e.currentTarget.value })} />
                  <Input class="w-20" value={it().ipiPercent} title="IPI %" placeholder="IPI %" onInput={(e) => mudar(i, { ipiPercent: e.currentTarget.value })} />
                  <Input class="w-40" value={it().prazoEntrega} title="Prazo de entrega" placeholder="Prazo (ex.: IMEDIATO)" onInput={(e) => mudar(i, { prazoEntrega: e.currentTarget.value })} />
                  <Input class="flex-1 min-w-48" value={it().especificacao} title="Especificação (técnica)" placeholder="Especificação técnica (bullets)" onInput={(e) => mudar(i, { especificacao: e.currentTarget.value })} />
                </div>
              </div>
            )}
          </Index>
          <div class="relative px-3 py-2">
            <Input placeholder="Adicionar modelo…" value={buscaModelo()} onInput={(e) => setBuscaModelo(e.currentTarget.value)} />
            <Show when={achadosModelo().length > 0}>
              <div class="absolute z-20 mt-1 w-80 overflow-hidden rounded-md border border-edge bg-panel shadow-lg">
                <For each={achadosModelo()}>
                  {(m) => (
                    <button type="button" class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-ink hover:bg-hover" onClick={() => adicionar(m)}>
                      <span>{m.codigoModelo}</span>
                      <span class="text-xs text-ink-subtle">saldo {num(m.saldoAtual)} · {brl(m.precoVendaPadrao)}</span>
                    </button>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Campo rotulo="Forma de pagamento">
          <Selecao value={forma()} onChange={(e) => setForma(e.currentTarget.value)}>
            <option value="">—</option>
            <For each={FORMAS}>{(f) => <option value={f}>{f}</option>}</For>
          </Selecao>
        </Campo>
        <Campo class="sm:col-span-2" rotulo="Condição de pagamento">
          <Input value={condicao() ?? ''} onInput={(e) => setCondicao(e.currentTarget.value)} placeholder="ex.: 30/60/90 dias" />
        </Campo>
        <Campo rotulo="Desconto geral (BRL)"><Input value={desconto()} onInput={(e) => setDesconto(e.currentTarget.value)} /></Campo>
      </div>
      <Campo rotulo="Observações"><AreaTexto value={obs() ?? ''} onInput={(e) => setObs(e.currentTarget.value)} /></Campo>
    </Formulario>
  )
}
